import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Space } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { DeactivateUserModal, useConfirmAction } from '@viq-admin/ui';
import { useAbility } from '../../ability/AbilityContext';
import { deactivateUser, reactivateUser } from '../../api/users';

/**
 * The user page's header actions: Deactivate (or Reactivate) and Edit.
 *
 * Edit used to live only in the users list's row menu, because this page had
 * nothing of its own; it is here now, and opens the existing edit route
 * (`/users/:oid/edit`, name / role / conferences). Deactivate and Reactivate
 * moved up from the danger zone so the everyday lifecycle actions sit with
 * the record. Purge stays in the danger zone at the bottom of the page.
 *
 * Everything here is gated on `manage User`, the gate the danger zone and the
 * list's row menu use; a `super_admin_readonly` sees no actions at all. The
 * backend enforces the same (`requireAbility('update', 'User')` on
 * deactivate / reactivate / PATCH).
 *
 * Deactivate opens the shared `DeactivateUserModal` with the super-admin
 * `deactivateUser` injected, exactly as the danger zone did. Reactivate asks
 * first, with the same wording as the users list's Reactivate: one click in a
 * header is easy to misplace and restores sign-in at once.
 *
 * A "Reset password" action (viq-admin#107, on hold until the Graph
 * permissions land) belongs between the lifecycle button and Edit.
 */
export function UserHeaderActions({
  oid,
  displayName,
  isInactive,
  onChanged,
}: {
  oid: string;
  displayName: string;
  isInactive: boolean;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const ability = useAbility();
  const confirm = useConfirmAction();
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  if (!ability.can('manage', 'User')) return null;

  function reactivate() {
    confirm({
      title: `Reactivate ${displayName}?`,
      content: `${displayName} will regain access immediately.`,
      okText: 'Reactivate',
      successMessage: `${displayName} reactivated.`,
      onConfirm: async () => {
        await reactivateUser(oid);
        onChanged();
      },
    });
  }

  // Its own wrapping Space: PageHeader lays out `actions` as one item, so the
  // buttons need their own gap, and on a phone they wrap under the title.
  // The modal sits outside it so a closed modal adds no empty Space item.
  return (
    <>
      <Space size={8} wrap>
        {isInactive ? (
          <Button onClick={reactivate}>Reactivate</Button>
        ) : (
          <Button danger onClick={() => setDeactivateOpen(true)}>
            Deactivate
          </Button>
        )}
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/users/${oid}/edit`)}
        >
          Edit
        </Button>
      </Space>
      <DeactivateUserModal
        oid={oid}
        displayName={displayName}
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        onSuccess={onChanged}
        onDeactivate={deactivateUser}
      />
    </>
  );
}
